import React, { useState, useEffect, useCallback } from "react";
import {
  FiUpload,
  FiDownload,
  FiCheckCircle,
  FiAlertCircle,
  FiSearch,
  FiTrash2,
  FiUsers,
  FiFlag,
} from "react-icons/fi";
import {
  postRequestAuthenticated,
  postRequestAuthenticatedWithFile,
} from "../../../services/rest";
import RolesPanel from "../widgets/RolesPanel";
import ConfirmDialog from "../../common/ConfirmDialog";
import EmptyState from "../widgets/EmptyState";
import { SkeletonList } from "../widgets/Skeleton";

const PAGE_SIZE = 10;
const SOURCE_LABELS = {
  import: "Imported",
  onboard: "Self-joined",
  autologin: "Auto-login",
  google: "Google",
  otp: "Email",
  jwt: "SSO",
  trusted: "Trusted",
};

const TEMPLATE =
  "email,phone,name,external_id,image_url,channels\n" +
  "sara@acme.com,+15551234567,Sara Lee,1001,,general;help\n" +
  "marcus@acme.com,,,,,general\n" +
  ",+15559876543,,,,general\n";

const MembersTab = ({ business }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  // Members list
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [flaggingId, setFlaggingId] = useState(null);
  // In-app confirm dialog (replaces native window.confirm).
  const [confirm, setConfirm] = useState(null); // {title, message, confirmLabel, danger, action}
  const [confirmBusy, setConfirmBusy] = useState(false);

  const runConfirm = async () => {
    if (!confirm?.action) return;
    setConfirmBusy(true);
    try {
      await confirm.action();
    } finally {
      setConfirmBusy(false);
      setConfirm(null);
    }
  };

  const fetchMembers = useCallback(async (opts = {}) => {
    const pg = opts.page || 1;
    const term = opts.search !== undefined ? opts.search : "";
    setListLoading(true);
    try {
      const res = await postRequestAuthenticated("/members/list", {
        search: term,
        page: pg,
        limit: PAGE_SIZE,
      });
      if (res?.success) {
        setMembers(res.members || []);
        setTotal(res.total || 0);
        setPage(pg);
      }
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers({ page: 1, search: "" });
  }, [fetchMembers]);

  const runSearch = () => fetchMembers({ page: 1, search });

  const doRemove = async (memberId) => {
    setRemovingId(memberId);
    try {
      const res = await postRequestAuthenticated("/members/remove", {
        memberId,
      });
      if (res?.success) {
        setMembers((prev) => prev.filter((m) => m._id !== memberId));
        setTotal((t) => Math.max(0, t - 1));
      }
    } finally {
      setRemovingId(null);
    }
  };

  const removeMember = (m) =>
    setConfirm({
      title: "Remove member",
      message: `Remove ${m.name || m.username} from your community? They'll lose access to all channels.`,
      confirmLabel: "Remove",
      danger: true,
      action: () => doRemove(m._id),
    });

  // Flag / unflag a member — a flagged member can read but not post anywhere.
  const doToggleFlag = async (m, willFlag) => {
    setFlaggingId(m._id);
    try {
      const res = await postRequestAuthenticated(
        willFlag ? "/flag/user" : "/unflag/user",
        { userId: m._id }
      );
      if (res?.success) {
        setMembers((prev) =>
          prev.map((x) => (x._id === m._id ? { ...x, flagged: willFlag } : x))
        );
      }
    } finally {
      setFlaggingId(null);
    }
  };

  const toggleFlag = (m) => {
    if (m.flagged) {
      // Unrestricting is low-risk — do it immediately, no confirm.
      doToggleFlag(m, false);
      return;
    }
    setConfirm({
      title: "Restrict member",
      message: `Restrict ${m.name || m.username}? They'll be able to read but won't be able to post in chats or DMs until you unrestrict them.`,
      confirmLabel: "Restrict",
      danger: true,
      action: () => doToggleFlag(m, true),
    });
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "members-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const doImport = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await postRequestAuthenticatedWithFile("/import/members", fd);
      setResult(res);
      if (res?.success) {
        setFile(null);
        fetchMembers({ page: 1, search: "" });
        setSearch("");
      }
    } catch (e) {
      setResult({ success: false, message: "Import failed. Try again." });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-7 sm:px-6 lg:px-8">
      <div className="mb-7">
        <h1 className="text-2xl font-semibold text-theme-secondaryText">
          Members
        </h1>
        <p className="mt-1 text-sm text-theme-placeholder">
          Bulk-import your members from a CSV or Excel file.
        </p>
      </div>

      <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-theme-secondaryText">
            Import members
          </h3>
          <button
            onClick={downloadTemplate}
            className="flex items-center gap-1.5 rounded-lg border border-theme-chatDivider px-3 py-1.5 text-xs font-semibold text-theme-primaryText transition-colors hover:bg-theme-tertiaryBackground"
          >
            <FiDownload size={14} /> Template
          </button>
        </div>
        <p className="mb-4 text-xs text-theme-placeholder">
          <span className="font-mono">email</span> or{" "}
          <span className="font-mono">phone</span> is required (one of them).
          Optional: <span className="font-mono">name</span> (auto-filled from the
          email, or <span className="font-mono">guest####</span> if only a phone
          is given), <span className="font-mono">external_id</span> (your own user
          id), <span className="font-mono">image_url</span>,{" "}
          <span className="font-mono">channels</span> (comma/semicolon-separated
          channel names to auto-join).
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2.5 text-sm text-theme-secondaryText transition-colors hover:bg-theme-sidebarHighlight">
            <FiUpload size={16} />
            {file ? file.name : "Choose CSV / XLSX"}
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                setResult(null);
              }}
            />
          </label>
          <button
            onClick={doImport}
            disabled={!file || importing}
            className="rounded-lg bg-theme-accent px-5 py-2.5 text-sm font-semibold text-theme-accentText transition-colors hover:bg-theme-accentHover disabled:opacity-50"
          >
            {importing ? "Importing…" : "Import"}
          </button>
        </div>

        {result && (
          <div className="mt-5 rounded-xl border border-theme-chatDivider bg-theme-tertiaryBackground p-4">
            {result.success ? (
              <>
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-theme-success">
                  <FiCheckCircle size={18} /> Processed {result.total} rows
                </div>
                <p className="text-sm text-theme-secondaryText">
                  {result.created} created · {result.updated} updated ·{" "}
                  {result.errors?.length || 0} issues
                </p>
                {result.errors?.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto text-xs text-theme-warning">
                    {result.errors.map((er, i) => (
                      <div key={i}>
                        Row {er.row}: {er.reason}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm text-theme-error">
                <FiAlertCircle size={18} /> {result.message || "Import failed"}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Members list */}
      <div className="mt-5 rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-theme-secondaryText">
            All members{" "}
            <span className="text-theme-placeholder">({total})</span>
          </h3>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-1.5">
              <FiSearch size={14} className="text-theme-placeholder" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                placeholder="Search name, email, id…"
                className="w-44 bg-transparent text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
              />
            </div>
            <button
              onClick={runSearch}
              className="rounded-lg border border-theme-chatDivider px-3 py-1.5 text-xs font-semibold text-theme-primaryText transition-colors hover:bg-theme-tertiaryBackground"
            >
              Search
            </button>
          </div>
        </div>

        {listLoading && members.length === 0 ? (
          <SkeletonList rows={6} />
        ) : members.length === 0 ? (
          <EmptyState
            icon={FiUsers}
            title="No members yet"
            subtitle="Members show up here as people join your community — or import them from a CSV above."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-theme-chatDivider text-xs uppercase tracking-wide text-theme-placeholder">
                  <th className="py-2 pr-3 font-semibold">Member</th>
                  <th className="py-2 pr-3 font-semibold">Source</th>
                  <th className="py-2 pr-3 font-semibold">Channels</th>
                  <th className="py-2 pr-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr
                    key={m._id}
                    className="border-b border-theme-chatDivider/60 last:border-b-0"
                  >
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-3">
                        <span
                          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-gray-900"
                          style={{ background: m.color_logo || "#C4C4C4" }}
                        >
                          {(m.name || m.username || "?")
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate font-medium text-theme-secondaryText">
                              {m.name || m.username}
                            </span>
                            {m.flagged && (
                              <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-theme-error/15 px-2 py-0.5 text-[10px] font-semibold text-theme-error">
                                <FiFlag size={9} /> Restricted
                              </span>
                            )}
                          </div>
                          <div className="truncate text-xs text-theme-placeholder">
                            {m.email || m.external_id || "—"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-theme-placeholder">
                      {SOURCE_LABELS[m.source] || m.source || "—"}
                    </td>
                    <td className="py-3 pr-3 text-theme-placeholder">
                      {m.channels && m.channels.length
                        ? m.channels.slice(0, 3).join(", ") +
                          (m.channels.length > 3
                            ? ` +${m.channels.length - 3}`
                            : "")
                        : "—"}
                    </td>
                    <td className="py-3 pr-1 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => toggleFlag(m)}
                          disabled={flaggingId === m._id}
                          title={m.flagged ? "Unrestrict member" : "Restrict (flag) member"}
                          className={`rounded-md p-1.5 transition-colors hover:bg-theme-tertiaryBackground disabled:opacity-50 ${
                            m.flagged
                              ? "text-theme-error"
                              : "text-theme-placeholder hover:text-theme-secondaryText"
                          }`}
                        >
                          <FiFlag size={15} />
                        </button>
                        <button
                          onClick={() => removeMember(m)}
                          disabled={removingId === m._id}
                          title="Remove member"
                          className="rounded-md p-1.5 text-theme-placeholder transition-colors hover:bg-theme-tertiaryBackground hover:text-theme-error disabled:opacity-50"
                        >
                          <FiTrash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {total > PAGE_SIZE && (
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-theme-placeholder">
                  Page {page} of {Math.ceil(total / PAGE_SIZE)} · {total} members
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchMembers({ page: page - 1, search })}
                    disabled={listLoading || page <= 1}
                    className="rounded-lg border border-theme-chatDivider px-3 py-1.5 text-xs font-semibold text-theme-primaryText transition-colors hover:bg-theme-tertiaryBackground disabled:opacity-40"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => fetchMembers({ page: page + 1, search })}
                    disabled={listLoading || page >= Math.ceil(total / PAGE_SIZE)}
                    className="rounded-lg border border-theme-chatDivider px-3 py-1.5 text-xs font-semibold text-theme-primaryText transition-colors hover:bg-theme-tertiaryBackground disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <RolesPanel />

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        busy={confirmBusy}
        onConfirm={runConfirm}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
};

export default MembersTab;
